import { useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API_URL } from "@/lib/api";

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [wiping, setWiping] = useState(false);

  const load = () => api.get("/admin/payments").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const total = rows.filter((r) => r.payment_status === "paid").reduce((s, r) => s + (r.amount || 0), 0);
  const testCount = rows.filter((r) => r.payment_status !== "paid" || (r.session_id || "").startsWith("cs_test_") || r.historical).length;

  const wipeTest = async () => {
    if (!window.confirm(
      `Delete ${testCount} test / unpaid / historical transaction${testCount === 1 ? "" : "s"}?\n\n` +
      `Real paid Stripe transactions (live-mode) are preserved. This resets the paid flags on any contract that no longer has a matching paid transaction.`
    )) return;
    setWiping(true);
    try {
      const r = await api.post("/admin/payments/wipe-test");
      toast.success(`Removed ${r.data.deleted_transactions} test rows · ${r.data.contracts_reverted} contracts reset`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed.");
    } finally {
      setWiping(false);
    }
  };

  return (
    <div data-testid="admin-payments">
      <div className="label-mono mb-2">/ Stripe</div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display text-4xl font-bold tracking-tighter">Payments</h1>
        <div className="flex items-end gap-6">
          {testCount > 0 && (
            <button
              onClick={wipeTest}
              disabled={wiping}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 text-[10px] font-black tracking-widest disabled:opacity-50"
              data-testid="wipe-test-payments"
              title={`Delete ${testCount} test / unpaid transaction(s)`}
            >
              <Trash2 size={11}/> {wiping ? "CLEARING…" : `CLEAR ${testCount} TEST ROW${testCount === 1 ? "" : "S"}`}
            </button>
          )}
          <div>
            <div className="label-mono">Total Collected</div>
            <div className="font-display text-3xl font-bold gradient-text">${total.toFixed(2)}</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">{["Date","Type","Amount","Status","Session ID","Invoice"].map((h) => <th key={h} className="px-5 py-3 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-white/5">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`payment-${r.session_id}`}>
                <td className="px-5 py-3 text-zinc-400 font-mono text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 capitalize">{r.payment_type}</td>
                <td className="px-5 py-3 font-mono">${r.amount.toFixed(2)}</td>
                <td className="px-5 py-3"><span className={`badge ${r.payment_status === "paid" ? "success" : "muted"}`}>{r.payment_status}</span></td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500 truncate max-w-xs">{r.session_id}</td>
                <td className="px-5 py-3">
                  <div className="inline-flex items-center gap-2 justify-end">
                    <a href={`${API_URL}/admin/payments/${r.session_id}/invoice`} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-2" data-testid={`invoice-${r.session_id}`}><Download size={12}/></a>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete this ${r.payment_type} payment of $${r.amount.toFixed(2)}?\n\nIf no other paid transaction of this type remains on the contract, its paid flag will be reversed.`)) return;
                        try {
                          await api.delete(`/admin/payments/${r.id}`);
                          toast.success("Payment deleted.");
                          load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
                      }}
                      className="p-2 rounded-md text-red-400/70 hover:text-red-300 hover:bg-red-500/10"
                      title="Delete payment"
                      data-testid={`delete-payment-${r.id}`}
                    >
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-500">No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
